import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Users,
  Mail,
  Calendar,
  Globe,
  TrendingUp,
  ShoppingCart,
  Package,
  MessageSquare,
  User,
  Loader2,
  List,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { api } from '@services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ListDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: any;
  dataSourceId?: string;
}

const COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

const ListDetailModal: React.FC<ListDetailModalProps> = ({
  isOpen,
  onClose,
  list,
  dataSourceId,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'insights' | 'contacts'>('overview');
  const { getSelection } = useDataSourceStore();
  const selection = getSelection();
  const [contactsPage, setContactsPage] = useState(1);
  const contactsLimit = 20;

  // Fetch MORE contacts for Insights analysis (1000 contacts for better data)
  const insightsLimit = 1000;

  // ✅ Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Fetch contacts in this list
  const {
    data: contactsData,
    isLoading: isLoadingContacts,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: ['list-contacts', list?.campaignId, contactsPage],
    queryFn: async () => {
      if (!list?.campaignId || !selection.companyId) return null;

      // Use provided dataSourceId or find GetResponse data source
      let targetDataSourceId = dataSourceId;

      if (!targetDataSourceId) {
        const emailDataSource = selection.allSelected?.find(
          (ds: any) => ds.type === 'getresponse' || ds.name?.toLowerCase().includes('getresponse')
        );
        targetDataSourceId = emailDataSource?.id;
      }

      if (!targetDataSourceId) {
        console.error('No data source ID found for fetching contacts');
        return null;
      }

      console.log('🔍 Fetching contacts:', {
        campaignId: list.campaignId,
        dataSourceId: targetDataSourceId,
        page: contactsPage,
        limit: contactsLimit,
      });

      const response = await api.get(`/api/v1/getresponse/contacts`, {
        params: {
          companyId: selection.companyId,
          dataSourceId: targetDataSourceId,
          campaignId: list.campaignId,
          page: contactsPage,
          limit: contactsLimit,
          sortBy: 'createdOn',
          sortOrder: 'desc',
        },
      });

      console.log('✅ Contacts response:', response.data);
      return response.data.data;
    },
    enabled: isOpen && !!list?.campaignId && !!selection.companyId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const contacts = contactsData?.contacts || [];
  const totalContacts = contactsData?.total || 0;
  const totalPages = Math.ceil(totalContacts / contactsLimit);

  // 🚀 Fetch COMPREHENSIVE insights (ALL contacts linked to e-shop data!)
  const { data: comprehensiveInsightsData, isLoading: isLoadingInsights } = useQuery({
    queryKey: ['list-comprehensive-insights', list?.campaignId],
    queryFn: async () => {
      if (!list?.campaignId || !selection.companyId) return null;

      let targetDataSourceId = dataSourceId;

      if (!targetDataSourceId) {
        const emailDataSource = selection.allSelected?.find(
          (ds: any) => ds.type === 'getresponse' || ds.name?.toLowerCase().includes('getresponse')
        );
        targetDataSourceId = emailDataSource?.id;
      }

      if (!targetDataSourceId) return null;

      // Find e-shop data source - look at ALL available data sources, not just selected
      // This ensures we can link e-shop customers even if e-shop isn't currently "selected" in UI
      const company = selection.company;
      const eshopDataSource = company?.dataSources?.find(
        (ds: any) =>
          ds.type === 'postgresql' && ds.availableModules?.includes('eshop') && ds.isActive
      );
      const eshopDataSourceId = eshopDataSource?.id;

      console.log('🚀 Fetching COMPREHENSIVE insights (ALL contacts + e-shop data):', {
        campaignId: list.campaignId,
        dataSourceId: targetDataSourceId,
        eshopDataSourceId,
        foundEshopSource: eshopDataSource?.name,
      });

      const response = await api.get(`/api/v1/getresponse/lists/${list.campaignId}/insights`, {
        params: {
          companyId: selection.companyId,
          dataSourceId: targetDataSourceId,
          eshopDataSourceId,
        },
      });

      console.log('✅ Comprehensive insights fetched:', response.data);
      return response.data.data;
    },
    enabled: isOpen && !!list?.campaignId && !!selection.companyId && activeTab === 'insights',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // 🎯 Format comprehensive insights data for UI
  const insights = React.useMemo(() => {
    if (!comprehensiveInsightsData) return null;

    const data = comprehensiveInsightsData;

    // Transform demographics data
    const demographics = data.demographics || {};
    const genderData = Object.entries(demographics.byGender || {}).map(([name, value]) => ({
      name,
      value,
    }));
    const ageData = Object.entries(demographics.byAgeGroup || {})
      .filter(([, value]) => (value as number) > 0)
      .map(([name, value]) => ({ name, value }));

    // Transform cart status data
    const cartStatusData = [
      { name: 'Abandoned', value: data.carts?.abandoned || 0 },
      { name: 'Active', value: data.carts?.active || 0 },
      { name: 'Converted', value: data.carts?.converted || 0 },
    ].filter(item => item.value > 0);

    // Transform customer status data
    const customerStatusData = Object.entries(data.customerStatus || {})
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .filter(item => item.value > 0);

    // Transform marketing consent data
    const consentData = data.marketingConsent
      ? [
          { name: 'Consented', value: data.marketingConsent.yes || 0 },
          { name: 'Not Consented', value: data.marketingConsent.no || 0 },
        ].filter(item => item.value > 0)
      : [];

    return {
      overview: data.overview,
      revenue: data.revenue,
      orders: data.orders,
      carts: data.carts,
      consultations: data.consultations,
      signups: data.signups,
      gender: genderData,
      age: ageData,
      avgAge: demographics.avgAge || 0,
      cartStatus: cartStatusData,
      customerStatus: customerStatusData,
      totalOrders: data.orders?.total || 0,
      totalRevenue: data.revenue?.total || 0,
      avgOrderValue: data.orders?.avgOrderValue || 0,
      tiers: customerStatusData,
      consent: consentData,
    };
  }, [comprehensiveInsightsData]);

  if (!isOpen || !list) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-85"
          onClick={onClose}
        />

        {/* Modal panel - Properly Centered */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col z-10">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{list.name}</h3>
                <p className="text-sm text-indigo-100">
                  {(
                    insights?.overview?.totalContactsInList ||
                    totalContacts ||
                    list.contactsCount ||
                    0
                  ).toLocaleString()}{' '}
                  contacts
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs - Like Contact Modal */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <nav className="flex space-x-1 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <List className="h-4 w-4" />
                  <span>Overview</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'insights'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>Insights</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Clean Card-Based Design - Like Contact Modal */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <List className="h-5 w-5 text-indigo-600" />
                    List Information
                  </h3>

                  {/* List Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/10 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">
                            Total Contacts
                          </p>
                          <p className="text-3xl font-bold text-indigo-900 dark:text-indigo-100 mt-1">
                            {totalContacts > 0
                              ? totalContacts.toLocaleString()
                              : list.contactsCount || 0}
                          </p>
                        </div>
                        <Users className="h-10 w-10 text-indigo-600 dark:text-indigo-400 opacity-50" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide">
                            Status
                          </p>
                          <p className="text-3xl font-bold text-green-900 dark:text-green-100 mt-1">
                            Active
                          </p>
                        </div>
                        <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400 opacity-50" />
                      </div>
                    </div>
                  </div>

                  {/* List Details */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 py-2 border-b border-gray-200 dark:border-gray-700">
                      <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Language
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {list.languageCode || 'EN'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 py-2 border-b border-gray-200 dark:border-gray-700">
                      <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Created
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {list.createdAt ? new Date(list.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {list.description && (
                      <div className="flex items-start gap-3 py-2">
                        <MessageSquare className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Description
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                            {list.description}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setActiveTab('contacts')}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Users className="h-5 w-5" />
                    View All Contacts
                  </button>
                  <button
                    onClick={() => setActiveTab('insights')}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <TrendingUp className="h-5 w-5" />
                    View Insights
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="space-y-6">
                {isLoadingInsights ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                      Loading comprehensive insights for ALL {totalContacts.toLocaleString()}{' '}
                      contacts + e-shop data...
                    </p>
                  </div>
                ) : !insights ? (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      No data available for insights
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Make sure an e-shop data source is connected
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Info: Comprehensive Analysis */}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-green-600 dark:text-green-400 mt-0.5 text-xl">✅</div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">
                            🚀 Comprehensive E-Commerce Analytics
                          </h4>
                          <p className="text-xs text-green-700 dark:text-green-400 mb-2">
                            📊 Analyzing{' '}
                            <strong>
                              {insights.overview?.totalContactsInList?.toLocaleString() ||
                                totalContacts.toLocaleString()}
                            </strong>{' '}
                            email contacts linked to{' '}
                            <strong>
                              {insights.overview?.totalCustomersWithEshopData?.toLocaleString() ||
                                0}
                            </strong>{' '}
                            e-shop customers
                          </p>
                          <p className="text-xs text-green-700 dark:text-green-400">
                            🎯 Conversion Rate:{' '}
                            <strong>{insights.overview?.conversionRate?.toFixed(1) || 0}%</strong>{' '}
                            of contacts have made purchases
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Key Metrics - E-Commerce Performance */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Total Orders</p>
                            <p className="text-xl font-bold mt-1">
                              {insights.totalOrders?.toLocaleString() || 0}
                            </p>
                          </div>
                          <ShoppingCart className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Total Revenue</p>
                            <p className="text-xl font-bold mt-1">
                              £{insights.totalRevenue?.toLocaleString() || 0}
                            </p>
                          </div>
                          <TrendingUp className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Avg Order Value</p>
                            <p className="text-xl font-bold mt-1">
                              £{Math.round(insights.avgOrderValue || 0).toLocaleString()}
                            </p>
                          </div>
                          <Package className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Abandoned Carts</p>
                            <p className="text-xl font-bold mt-1">
                              {insights.carts?.abandoned?.toLocaleString() || 0}
                            </p>
                          </div>
                          <ShoppingCart className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Customers</p>
                            <p className="text-xl font-bold mt-1">
                              {insights.overview?.totalCustomersWithEshopData?.toLocaleString() ||
                                0}
                            </p>
                          </div>
                          <Users className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Total Contacts</p>
                            <p className="text-xl font-bold mt-1">
                              {(
                                insights?.overview?.totalContactsInList ||
                                totalContacts ||
                                list.contactsCount ||
                                0
                              ).toLocaleString()}
                            </p>
                          </div>
                          <Mail className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Abandoned Signups</p>
                            <p className="text-xl font-bold mt-1">
                              {insights.signups?.abandoned?.toLocaleString() || 0}
                            </p>
                            <p className="text-xs opacity-75 mt-1">
                              Signed up but never returned (&gt;1 day)
                            </p>
                          </div>
                          <AlertCircle className="h-7 w-7 opacity-80" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs opacity-90">Abandoned Consultations</p>
                            <p className="text-xl font-bold mt-1">
                              {insights.consultations?.abandoned?.toLocaleString() || 0}
                            </p>
                          </div>
                          <AlertCircle className="h-7 w-7 opacity-80" />
                        </div>
                      </div>
                    </div>

                    {/* Charts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Gender Distribution */}
                      {insights.gender && insights.gender.length > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Gender Distribution
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={insights.gender}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={entry => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {insights.gender.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Age Distribution */}
                      {insights.age && insights.age.length > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Age Distribution
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={insights.age}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#6366f1" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Cart Status */}
                      {insights.cartStatus && insights.cartStatus.length > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Cart Status
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={insights.cartStatus}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={entry => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {insights.cartStatus.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Customer Tiers */}
                      {insights.tiers && insights.tiers.length > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Customer Tiers
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={insights.tiers}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#10b981" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Marketing Consent */}
                      {insights.consent && insights.consent.length > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Marketing Consent
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={insights.consent}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={entry => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {insights.consent.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Consultation Activity */}
                      {insights.consultations && insights.consultations.total > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Consultation Activity
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart
                              data={[
                                { name: 'Total', value: insights.consultations.total },
                                { name: 'Completed', value: insights.consultations.completed },
                                { name: 'Abandoned', value: insights.consultations.abandoned },
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#ec4899" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Revenue Performance */}
                      <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Revenue Performance
                        </h4>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={[
                              {
                                name: 'Total Revenue',
                                value: insights.totalRevenue,
                                label: `£${insights.totalRevenue.toLocaleString()}`,
                              },
                              {
                                name: 'Avg Order',
                                value: insights.avgOrderValue,
                                label: `£${insights.avgOrderValue.toFixed(2)}`,
                              },
                              {
                                name: 'Total Orders',
                                value: insights.totalOrders,
                                label: insights.totalOrders.toLocaleString(),
                              },
                            ]}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Order Activity */}
                      {insights.orders && insights.orders.total > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Order Activity
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Completed', value: insights.orders.total },
                                  {
                                    name: 'Avg Value',
                                    value: Math.round(insights.orders.avgOrderValue),
                                  },
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={entry => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                <Cell fill="#10b981" />
                                <Cell fill="#3b82f6" />
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Abandoned Cart Analysis */}
                      {insights.carts && insights.carts.total > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Cart Performance
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart
                              data={[
                                {
                                  name: 'Abandoned',
                                  value: insights.carts.abandoned,
                                  fill: '#ef4444',
                                },
                                { name: 'Active', value: insights.carts.active, fill: '#f59e0b' },
                                {
                                  name: 'Converted',
                                  value: insights.carts.converted,
                                  fill: '#10b981',
                                },
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="value" fill="#8884d8">
                                {[
                                  { name: 'Abandoned', value: insights.carts.abandoned },
                                  { name: 'Active', value: insights.carts.active },
                                  { name: 'Converted', value: insights.carts.converted },
                                ].map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      entry.name === 'Abandoned'
                                        ? '#ef4444'
                                        : entry.name === 'Active'
                                        ? '#f59e0b'
                                        : '#10b981'
                                    }
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Abandoned</p>
                              <p className="text-lg font-bold text-red-600 dark:text-red-400">
                                {insights.carts.abandoned.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
                              <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                                {insights.carts.active.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Converted</p>
                              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                                {insights.carts.converted.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Signup Activity Analysis */}
                      {insights.signups && insights.signups.total > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Signup Activity
                          </h4>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart
                              data={[
                                { name: 'Total Signups', value: insights.signups.total },
                                { name: 'Recent (30d)', value: insights.signups.recent },
                                { name: 'Abandoned', value: insights.signups.abandoned },
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#8b5cf6" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListDetailModal;
